import { useEffect, useRef } from "react";

/**
 * Visibility-aware polling hook.
 *
 * Behaviour:
 *   • Fires `callback()` immediately on mount (if `runOnMount`).
 *   • Schedules a `setInterval` to fire every `ms` milliseconds.
 *   • PAUSES when the document is hidden (background tab). Resumes on visibility.
 *     → When resuming, fires `callback()` immediately so the UI catches up,
 *       then continues the regular interval.
 *   • If `enabled === false`, no interval is created (still respects cleanup).
 *   • Captures the latest `callback` reference via a ref → effect deps stay stable
 *     so users can pass inline arrow functions without re-installing the interval
 *     on every render.
 *
 * Usage:
 *   useInterval(() => api.get("/x").then(setX), 8000);
 *   useInterval(loadFn, 5000, { enabled: someFlag, runOnMount: false });
 *
 * NOTE: This intentionally does NOT use AbortController — our polling responses
 * are small and the next poll naturally replaces any stale data.
 */
export function useInterval(callback, ms, opts = {}) {
  const { enabled = true, runOnMount = true } = opts;
  const cbRef = useRef(callback);

  // Always keep the latest callback in the ref (no effect dep on it)
  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || !ms || ms <= 0) return undefined;

    let timer = null;
    const fire = () => {
      try { cbRef.current?.(); } catch (_) { /* swallow */ }
    };
    const start = () => {
      if (timer) return;
      timer = setInterval(fire, ms);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Catch up immediately on resume, then resume interval
        fire();
        start();
      }
    };

    if (runOnMount) fire();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [ms, enabled, runOnMount]);
}

export default useInterval;
